import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DatePicker from 'components/DatePicker/DatePicker';
import NumberField from 'components/NumberField/NumberField';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import { DATA_SETS_ENUM_TYPES } from 'constants/dataSets';
import {
  CODE,
  CRITICALITY_FROM_DOWNSTREAM_WASTE,
  CRITICALITY_RATING,
  DATE_CLOSED,
  DATE_OPENED,
  NAME,
  PRODUCT_CATEGORY_ID,
  PRODUCT_TYPE,
  RELIANCE_ON_FREE_NATURAL_RESOURCES,
  RELIANCE_ON_FREE_SOCIAL_RESOURCES,
  UNIQUE_IDENTIFIER,
  UPSTREAM_TIER,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
  VOLUME_QTY,
  VOLUME_QTY_UNIT,
} from 'constants/formFields';
import { DATE_FORMAT } from 'constants/general';
import { NUMBER_FORMATS } from 'constants/interfaces';
import {
  PRODUCT_TYPE_OPTIONS,
} from 'utils/formHelpers/productTypeValueChain';

import useProductTypeValueChain from '../hooks/useProductTypeValueChain';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IFieldError from 'store/types/IFieldError';
import type { IProductGeneralFields } from '../interfaces/IProductGeneralFields';

import styles from '../sass/CreateProductView.module.scss';

function ProductGeneralFields({
  control,
  enumData,
  dateOpened,
  onDateOpenedChange,
  recordId,
  isUniqueIdentifierEditable = false,
  vcOptions,
  handleGetVcOptions,
  vcOptionsList,
  listPosition,
  setValue,
  vcDisabled,
  productCategoriesOptions,
  isProductTypeDisabled = false,
}: IProductGeneralFields) {
  const { t } = useTranslation();

  const sortEnumDataCriticalityRating = enumData?.[DATA_SETS_ENUM_TYPES.criticalityRating]?.sort((a, b) => {
    const order: Record<string, number> = { low: 1, medium: 2, high: 3 };
    const valueA = a.value.toLowerCase();
    const valueB = b.value.toLowerCase();
    return (valueA in order ? order[valueA] : 999) - (valueB in order ? order[valueB] : 999);
  });

  const {
    handleChangeProductType,
    handleChangeValueChain,
  } = useProductTypeValueChain({
    control,
    handleGetVcOptions,
    setValue,
    vcOptionsList,
  });

  return (
    <DataSetsPanel title={t('products.selectionsGeneral')}>

      <Controller
        control={control}
        name={NAME}
        render={({
          field: {
            name,
            value,
            onChange,
          },
          fieldState: { error },
        }) => (
          <TextField
            name={name}
            value={value}
            label={t('products.fields.productName')}
            error={error?.message}
            onChange={(event) => onChange(event.target.value)}
            required
            placeholder={t('products.fields.productName')}
          />
        )}
      />
      <Controller
        control={control}
        name={UNIQUE_IDENTIFIER}
        render={({
          field: {
            name,
            value,
            onChange,
          },
          fieldState: { error },
        }) => (
          <TextField
            name={name}
            value={value}
            label={t('products.fields.uniqueIdentifier')}
            error={error?.message}
            onChange={(event) => onChange(event.target.value)}
            required
            placeholder={t('products.fields.uniqueIdentifier')}
            disabled={!!recordId && !isUniqueIdentifierEditable}
          />
        )}
      />
      <Controller
        control={control}
        name={CODE}
        render={({
          field: {
            name,
            value,
            onChange,
          },
          fieldState: { error },
        }) => (
          <TextField
            name={name}
            value={value}
            label={t('products.fields.productCode')}
            error={error?.message}
            onChange={(event) => onChange(event.target.value)}
            required
            placeholder={t('products.fields.productCode')}
          />
        )}
      />
      <Controller
        control={control}
        name={PRODUCT_CATEGORY_ID}
        render={({
          field: { name, value, onChange },
          fieldState: { error },
        }) => (
          <Select
            name={name}
            label={t('products.fields.productCategory')}
            placeholder={t('products.fields.productCategory')}
            options={productCategoriesOptions}
            value={value?.value ? value : undefined}
            onChange={onChange}
            error={(error as IFieldError)?.value?.message}
            menuPosition="absolute"
          />
        )}
      />
      <div className={styles.dateRow}>
        <Controller
          control={control}
          name={VALUE_CHAIN_LIST}
          render={({
            field: { name, value: valueChain, onChange: onChangeValueChain },
            fieldState: { error: errorCurrency },
          }) => (
            <Select
              name={name}
              placeholder={t('common.valueChainPosition')}
              label={t('common.valueChainPosition')}
              options={vcOptionsList}
              value={valueChain?.value ? valueChain : undefined}
              onChange={(value) => {
                const selectedValue = value as IOption;
                handleChangeValueChain(selectedValue, onChangeValueChain);
              }}
              error={errorCurrency?.message}
              menuPosition="absolute"
              isDisabled={vcDisabled}
              required
            />
          )}
        />
        <Controller
          control={control}
          name={VALUE_CHAIN_SECTION}
          render={({
            field: { name, value: valueChainSection, onChange: onChangeValueChainSection },
            fieldState: { error: errorCurrencySection },
          }) => (
            <Select
              name={name}
              placeholder={t('common.valueChainCategory')}
              label={t('common.valueChainCategory')}
              options={vcOptions || listPosition}
              value={valueChainSection?.value ? valueChainSection : null}
              onChange={onChangeValueChainSection}
              error={errorCurrencySection?.message}
              menuPosition="absolute"
              isDisabled={listPosition?.length === 0 && !recordId}
              required
            />
          )}
        />
      </div>
      <Controller
        control={control}
        name={PRODUCT_TYPE}
        render={({
          field: { name, value, onChange },
        }) => (
          <Select
            name={name}
            label={t('products.fields.productType')}
            placeholder={t('products.fields.productType')}
            options={PRODUCT_TYPE_OPTIONS}
            value={value?.value ? value : undefined}
            onChange={(selectedOption) => {
              const selectedProductType = selectedOption as IOption;
              handleChangeProductType(selectedProductType, onChange);
            }}
            menuPosition="absolute"
            isDisabled={isProductTypeDisabled}
          />
        )}
      />
      <Controller
        control={control}
        name={UPSTREAM_TIER}
        render={({
          field: {
            name,
            value,
            onChange,
          },
          fieldState: { error },
        }) => (
          <Select
            name={name}
            label={t('products.fields.upstreamTier')}
            placeholder={t('products.fields.upstreamTier')}
            options={enumData?.[DATA_SETS_ENUM_TYPES.upstreamTier]}
            value={value?.value ? value : undefined}
            onChange={onChange}
            error={(error as IFieldError)?.value?.message}
            menuPosition="absolute"
            required
          />
        )}
      />
      <div className={styles.dateRow}>
        <Controller
          control={control}
          name={DATE_OPENED}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <DatePicker
              label={t('products.fields.dateFrom')}
              error={error?.message}
              onChange={(date) => {
                onDateOpenedChange(date);
                onChange(date);
              }}
              name={name}
              value={value}
              placeholder={DATE_FORMAT}
              maxDate={new Date()}
              required
            />
          )}
        />
        <Controller
          control={control}
          name={DATE_CLOSED}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <DatePicker
              label={t('products.fields.dateTo')}
              error={error?.message}
              onChange={onChange}
              name={name}
              value={value}
              minDate={dateOpened || undefined}
              placeholder={DATE_FORMAT}
            />
          )}
        />
      </div>
      <div className={styles.volumeRow}>
        <Controller
          control={control}
          name={VOLUME_QTY}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <NumberField
              name={name}
              value={value}
              label={t('products.fields.volume')}
              placeholder={t('products.fields.volume')}
              error={error?.message}
              onChange={(values) => onChange(values.value)}
              required
            />
          )}
        />
        <Controller
          control={control}
          name={VOLUME_QTY_UNIT}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <Select
              name={name}
              label={t('products.fields.volumeUnit')}
              placeholder={t('products.fields.volumeUnit')}
              options={enumData?.[DATA_SETS_ENUM_TYPES.volumeQtyUnit]}
              value={value?.value ? value : undefined}
              onChange={onChange}
              error={(error as IFieldError)?.value?.message}
              menuPosition="absolute"
              required
            />
          )}
        />
      </div>
      <Controller
        control={control}
        name={RELIANCE_ON_FREE_NATURAL_RESOURCES}
        render={({
          field: {
            name,
            value,
            onChange,
          },
          fieldState: { error },
        }) => (
          <Select
            name={name}
            label={t('products.fields.relianceNatural')}
            placeholder={t('products.fields.placeholderNatural')}
            options={enumData?.[DATA_SETS_ENUM_TYPES.relianceOnFreeNaturalResources]}
            value={value || undefined}
            onChange={onChange}
            error={error?.message}
            menuPosition="absolute"
            isMulti
          />
        )}
      />
      <Controller
        control={control}
        name={RELIANCE_ON_FREE_SOCIAL_RESOURCES}
        render={({
          field: {
            name,
            value,
            onChange,
          },
          fieldState: { error },
        }) => (
          <Select
            name={name}
            label={t('products.fields.relianceSocial')}
            placeholder={t('products.fields.placeholderSocial')}
            options={enumData?.[DATA_SETS_ENUM_TYPES.relianceOnFreeSocialResources]}
            value={value || undefined}
            onChange={onChange}
            error={error?.message}
            menuPosition="absolute"
            isMulti
          />
        )}
      />
      <Controller
        control={control}
        name={CRITICALITY_RATING}
        render={({
          field: {
            name,
            value,
            onChange,
          },
          fieldState: { error },
        }) => (
          <Select
            name={name}
            label={t('products.fields.rating')}
            placeholder={t('products.fields.rating')}
            options={sortEnumDataCriticalityRating}
            value={value?.value ? value : undefined}
            onChange={onChange}
            error={(error as IFieldError)?.value?.message}
            menuPosition="absolute"
            required
          />
        )}
      />
      <Controller
        control={control}
        name={CRITICALITY_FROM_DOWNSTREAM_WASTE}
        render={({
          field: {
            name,
            value,
            onChange,
          },
          fieldState: { error },
        }) => (
          <NumberField
            name={name}
            value={value}
            label={t('products.fields.circularityPercentage')}
            error={error?.message}
            onChange={(values) => onChange(values.value)}
            type={NUMBER_FORMATS.percent}
            placeholder={t('products.fields.circularityPercentage')}
            allowNegative={false}
          />
        )}
      />
    </DataSetsPanel>
  );
}

export default ProductGeneralFields;
